import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import MicRecorder from "mic-recorder-to-mp3";
import toast from "react-hot-toast";
import { ReactComponent as StopRecordIcon } from "../../../assets/StopRecordIcon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/cross-color.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete-color.svg";
import useAudioWaveform from "./useAudioWaveform";
import "./VoiceRecorder.css";
import VoiceWaveTrack from "./VoiceWaveTrack";
import playIcon from "../../../assets/play.svg";
import pauseIcon from "../../../assets/pause.svg";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const MAX_DURATION_SEC = 300;

const formatTime = (value) => {
  const safe = Math.max(0, Math.floor(value || 0));
  const min = Math.floor(safe / 60);
  const sec = safe % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
};

const VoiceRecorder = React.forwardRef(
  (
    {
      recordUrl = null,
      isShared = false,
      onModeChange = () => {},
      onRecorded = () => {},
      onRecordCleared = () => {},
      onLimitReached = () => {},
      onDurationChange = () => {},
    },
    ref
  ) => {
    const [mode, setMode] = useState("idle");
    const [seconds, setSeconds] = useState(0);
    const timerRef = useRef(null);
    const modeRef = useRef("idle");
    const createdUrlRef = useRef(null);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playProgress, setPlayProgress] = useState(0);
    const audioRef = useRef(null);

    const {
      canvasRef,
      peaksRef,
      setProgress,
      startStream,
      stopStream,
      resetPeaks,
    } = useAudioWaveform();

    const changeMode = useCallback(
      (next) => {
        modeRef.current = next;
        setMode(next);
        onModeChange(next);
      },
      [onModeChange]
    );

    const stopTimer = useCallback(() => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, []);

    const teardownRecorder = useCallback(() => {
      stopTimer();
      stopStream();
      try {
        Mp3Recorder.stop();
        Mp3Recorder.getMp3();
      } catch (e) {}
    }, [stopTimer, stopStream]);

    const beginRecording = useCallback(async () => {
      if (isShared) return;

      setSeconds(0);
      setProgress(0);
      resetPeaks();
      setDuration(0);
      setIsPlaying(false);
      setPlayProgress(0);

      try {
        await Mp3Recorder.start();
      } catch (e) {
        console.error(e);
        toast.error("Не удалось получить доступ к микрофону");
        return;
      }

      startStream(Mp3Recorder);
      timerRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
      changeMode("recording");
    }, [isShared, resetPeaks, setProgress, startStream, changeMode]);

    const finishRecording = useCallback(async () => {
      if (modeRef.current !== "recording") return;

      stopTimer();
      stopStream();

      try {
        Mp3Recorder.stop();
        const [, blob] = await Mp3Recorder.getMp3();
        const url = URL.createObjectURL(blob);
        createdUrlRef.current = url;
        setProgress(0);
        onRecorded(blob, url);
        changeMode("recorded");
      } catch (e) {
        console.error(e);
        toast.error("Не удалось сохранить запись");
        changeMode("idle");
      }
    }, [changeMode, onRecorded, setProgress, stopStream, stopTimer]);

    const cancelRecording = useCallback(() => {
      teardownRecorder();
      setSeconds(0);
      setProgress(0);
      resetPeaks();
      setDuration(0);
      setIsPlaying(false);
      setPlayProgress(0);
      changeMode("idle");
    }, [changeMode, resetPeaks, setProgress, teardownRecorder]);

    useEffect(() => {
      if (mode !== "recording" || seconds < MAX_DURATION_SEC) return;
      onLimitReached();
      finishRecording();
    }, [mode, seconds, onLimitReached, finishRecording]);

    useEffect(() => {
      if (recordUrl || modeRef.current !== "recorded") return;
      setSeconds(0);
      setProgress(0);
      resetPeaks();
      setDuration(0);
      setIsPlaying(false);
      setPlayProgress(0);
      changeMode("idle");
    }, [recordUrl, changeMode, resetPeaks, setProgress]);

    const handleDelete = useCallback(() => {
      const url = createdUrlRef.current;
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      createdUrlRef.current = null;
      onRecordCleared();
      setSeconds(0);
      setProgress(0);
      resetPeaks();
      setDuration(0);
      setIsPlaying(false);
      setPlayProgress(0);
      changeMode("idle");
    }, [changeMode, onRecordCleared, resetPeaks, setProgress]);

    const handleRerecord = useCallback(() => {
      handleDelete();
      beginRecording();
    }, [beginRecording, handleDelete]);

    const togglePlay = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    };

    useImperativeHandle(ref, () => ({
      startRecording: () => {
        if (modeRef.current === "idle") beginRecording();
      },
      stopRecording: () => {
        if (modeRef.current === "recording") finishRecording();
      },
      restartRecording: () => {
        if (modeRef.current === "recorded") handleRerecord();
      },
      deleteRecording: () => {
        if (modeRef.current === "recorded") handleDelete();
      },
    }));

    useEffect(
      () => () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (modeRef.current === "recording") {
          try {
            Mp3Recorder.stop();
          } catch (e) {
            /* уже остановлен */
          }
        }
      },
      []
    );

    const isRecording = mode === "recording";
    const isRecorded = mode === "recorded";

    if (isRecorded) {
      return (
        <div className="voice-recorder recorded">
          <span className="voice-recorder-time">{formatTime(duration)}</span>

          <VoiceWaveTrack
            className="voice-recorder-track"
            audioRef={audioRef}
            src={recordUrl}
            peaks={peaksRef.current}
            progress={playProgress}
            onSeek={setPlayProgress}
          />

          <div className="voice-recorder-controls">
            <button
              className="voice-recorder-btn play"
              onClick={togglePlay}
              title={isPlaying ? "Пауза" : "Слушать"}
              aria-label={isPlaying ? "Пауза" : "Слушать"}
            >
              <img src={isPlaying ? pauseIcon : playIcon} alt="" />
            </button>
            <button
              className="voice-recorder-btn"
              onClick={handleRerecord}
              title="Перезаписать"
              aria-label="Перезаписать"
            >
              ↻
            </button>
            <button
              className="voice-recorder-btn"
              onClick={handleDelete}
              title="Удалить запись"
              aria-label="Удалить запись"
            >
              <DeleteIcon />
            </button>
          </div>

          <audio
            ref={audioRef}
            src={recordUrl}
            preload="metadata"
            onLoadedMetadata={(e) => {
              const value = e.target.duration || 0;
              setDuration(value);
              onDurationChange(value);
            }}
            onTimeUpdate={(e) => {
              const audio = e.currentTarget;
              if (!audio.duration) return;
              setPlayProgress(audio.currentTime / audio.duration);
            }}
            onEnded={() => {
              setIsPlaying(false);
              setPlayProgress(0);
            }}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            hidden
          />
        </div>
      );
    }

    if (isRecording) {
      return (
        <div className="voice-recorder recording">
          <span className="voice-recorder-badge">
            <span className="voice-recorder-dot" />
            REC
          </span>

          <span className="voice-recorder-time">{formatTime(seconds)}</span>

          <canvas ref={canvasRef} className="voice-recorder-canvas" />

          <div className="voice-recorder-controls">
            <button
              className="voice-recorder-btn"
              onClick={cancelRecording}
              title="Отменить запись"
              aria-label="Отменить запись"
            >
              <CloseIcon />
            </button>
            <button
              className="voice-recorder-btn stop"
              onClick={finishRecording}
              title="Остановить запись"
              aria-label="Остановить запись"
            >
              <StopRecordIcon />
            </button>
          </div>
        </div>
      );
    }

    return <div className="voice-recorder idle" />;

    // return (
    //   <div className={`voice-recorder ${mode}`}>
    //     {isRecording && (
    //       <span className="voice-recorder-badge">
    //         <span className="voice-recorder-dot" />
    //         REC
    //       </span>
    //     )}

    //     <span className="voice-recorder-time">
    //       {formatTime(isRecorded ? duration : seconds)}
    //     </span>

    //     <canvas ref={canvasRef} className="voice-recorder-canvas" />

    //     <div className="voice-recorder-controls">
    //       {isRecording ? (
    //         <>
    //           <button
    //             className="voice-recorder-btn"
    //             onClick={cancelRecording}
    //             title="Отменить запись"
    //             aria-label="Отменить запись"
    //           >
    //             <CloseIcon />
    //           </button>
    //           <button
    //             className="voice-recorder-btn stop"
    //             onClick={finishRecording}
    //             title="Остановить запись"
    //             aria-label="Остановить запись"
    //           >
    //             <StopRecordIcon />
    //           </button>
    //         </>
    //       ) : (
    //         <>
    //           <button
    //             className="voice-recorder-btn play"
    //             onClick={handlePlayToggle}
    //             title={isPlaying ? "Пауза" : "Слушать"}
    //             aria-label={isPlaying ? "Пауза" : "Слушать"}
    //           >
    //             <img src={isPlaying ? pauseIcon : playIcon} alt="" />
    //           </button>
    //           <button
    //             className="voice-recorder-btn"
    //             onClick={handleRerecord}
    //             title="Перезаписать"
    //             aria-label="Перезаписать"
    //           >
    //             ↻
    //           </button>
    //           <button
    //             className="voice-recorder-btn"
    //             onClick={handleDelete}
    //             title="Удалить запись"
    //             aria-label="Удалить запись"
    //           >
    //             <DeleteIcon />
    //           </button>
    //         </>
    //       )}
    //     </div>

    //     {isRecorded && (
    //       <audio
    //         ref={audioRef}
    //         src={recordUrl}
    //         hidden
    //         onLoadedMetadata={(e) => {
    //           const value = e.target.duration || 0;
    //           setDuration(value);
    //           onDurationChange(value);
    //         }}
    //         onTimeUpdate={(e) => {
    //           const audio = e.currentTarget;
    //           if (!audio.duration) return;
    //           setProgress(audio.currentTime / audio.duration);
    //         }}
    //         onEnded={() => {
    //           setIsPlaying(false);
    //           setProgress(0);
    //         }}
    //         onPause={() => setIsPlaying(false)}
    //         onPlay={() => setIsPlaying(true)}
    //       />
    //     )}
    //   </div>
    // );
  }
);

VoiceRecorder.displayName = "VoiceRecorder";

export default VoiceRecorder;
